import { FRUITS, SPECIAL_TYPES, OBSTACLE_TYPES } from "@/hooks/useGameStateV2";
import { cn } from "@/lib/utils";
import { useState, useEffect, memo } from "react";
import { FRUIT_3D_IMAGES, USE_3D_FRUITS } from "@/assets/fruitImages";

// Skin effect configurations for rendering
const SKIN_VISUALS = {
  // === SKINS (apply visual effects to specific fruits) ===
  golden_banana: {
    appliesTo: 'banana',
    overlayEmoji: '✨',
    glowClass: 'skin-glow-golden',
    bgClass: 'bg-gradient-to-br from-yellow-300/40 to-amber-500/40',
    borderClass: 'ring-2 ring-yellow-400/60',
    shimmer: true
  },
  crystal_apple: {
    appliesTo: 'apple',
    overlayEmoji: '💎',
    glowClass: 'skin-glow-crystal',
    bgClass: 'bg-gradient-to-br from-cyan-200/40 to-blue-400/40',
    borderClass: 'ring-2 ring-cyan-400/60',
    shimmer: true
  },
  neon_orange: {
    appliesTo: 'orange',
    overlayEmoji: '⚡',
    glowClass: 'skin-glow-neon',
    bgClass: 'bg-gradient-to-br from-orange-400/50 to-pink-500/40',
    borderClass: 'ring-2 ring-orange-400/80 animate-pulse',
    neonPulse: true
  },
  crystal_grape: {
    appliesTo: 'blueberry',
    overlayEmoji: '💎',
    glowClass: 'skin-glow-crystal',
    bgClass: 'bg-gradient-to-br from-cyan-200/40 to-blue-400/40',
    borderClass: 'ring-2 ring-cyan-400/60',
    shimmer: true
  },
  diamond_grape: {
    appliesTo: 'blueberry',
    overlayEmoji: '💠',
    glowClass: 'skin-glow-diamond',
    bgClass: 'bg-gradient-to-br from-purple-200/40 to-pink-400/40',
    borderClass: 'ring-2 ring-purple-400/60',
    shimmer: true
  },
  ruby_cherry: {
    appliesTo: 'cherry',
    overlayEmoji: '❤️',
    glowClass: 'skin-glow-ruby',
    bgClass: 'bg-gradient-to-br from-red-400/50 to-pink-500/40',
    borderClass: 'ring-2 ring-red-500/60',
    shimmer: true
  },
  plasma_pear: {
    appliesTo: 'pear',
    overlayEmoji: '⚡',
    glowClass: 'skin-glow-plasma',
    bgClass: 'bg-gradient-to-br from-green-300/50 to-cyan-400/40',
    borderClass: 'ring-2 ring-cyan-400/60 animate-pulse',
    shimmer: true
  },
  cosmic_melon: {
    appliesTo: 'watermelon',
    overlayEmoji: '🌟',
    glowClass: 'skin-glow-cosmic',
    bgClass: 'bg-gradient-to-br from-purple-500/40 to-indigo-600/40',
    borderClass: 'ring-2 ring-purple-400/60',
    shimmer: true
  },
  inferno_strawberry: {
    appliesTo: 'strawberry',
    overlayEmoji: '🔥',
    glowClass: 'skin-glow-inferno',
    bgClass: 'bg-gradient-to-br from-orange-500/50 to-red-600/40',
    borderClass: 'ring-2 ring-orange-500/60',
    shimmer: true
  },
  // === MEMBER EXCLUSIVE SKINS ===
  member_golden: {
    appliesTo: 'all',
    overlayEmoji: '👑',
    glowClass: 'skin-glow-member-golden',
    bgClass: 'bg-gradient-to-br from-yellow-400/50 to-amber-600/50',
    borderClass: 'ring-2 ring-yellow-500/80',
    shimmer: true,
    memberExclusive: true
  },
  member_diamond: {
    appliesTo: 'all',
    overlayEmoji: '💎',
    glowClass: 'skin-glow-member-diamond',
    bgClass: 'bg-gradient-to-br from-cyan-300/50 to-blue-500/50',
    borderClass: 'ring-2 ring-cyan-400/80',
    shimmer: true,
    memberExclusive: true
  },
  member_rainbow: {
    appliesTo: 'all',
    overlayEmoji: '🌈',
    glowClass: 'skin-glow-member-rainbow',
    bgClass: 'bg-gradient-to-br from-pink-400/40 via-purple-400/40 to-cyan-400/40',
    borderClass: 'ring-2 ring-purple-400/80 animate-pulse',
    shimmer: true,
    rainbowEffect: true,
    memberExclusive: true
  },
  void_berry: {
    appliesTo: 'blueberry',
    overlayEmoji: '🌀',
    glowClass: 'skin-glow-void',
    bgClass: 'bg-gradient-to-br from-gray-800/60 to-purple-900/50',
    borderClass: 'ring-2 ring-gray-500/60',
    shimmer: true
  },
  neon_fruits: {
    appliesTo: 'all',
    overlayEmoji: null,
    glowClass: 'skin-glow-neon',
    bgClass: null,
    borderClass: 'ring-2 ring-pink-500/50',
    neonPulse: true
  },
  legendary_fruit_set: {
    appliesTo: 'all',
    overlayEmoji: '👑',
    glowClass: 'skin-glow-legendary',
    bgClass: 'bg-gradient-to-br from-yellow-400/30 to-amber-500/30',
    borderClass: 'ring-2 ring-yellow-500/70',
    shimmer: true
  }
};

function FruitCellComponent({ 
  cell, 
  isSelected, 
  isAdjacent, 
  onClick, 
  disabled,
  isNew = false,
  isExploding = false,
  small = false, // For 10x10 grid
  extraSmall = false, // For 12x12 grid
  equippedSkin = null, // Skin from cosmetics
  equippedEffect = null, // Effect from cosmetics (fire_match, rainbow_burst, etc.)
}) {
  const [animationClass, setAnimationClass] = useState('');
  const [showParticles, setShowParticles] = useState(false);
  const [showEffectParticles, setShowEffectParticles] = useState(false);
  
  // Size helpers for different grid sizes
  const getEmojiSize = (base, smallSize, extraSmallSize) => {
    if (extraSmall) return extraSmallSize;
    if (small) return smallSize;
    return base;
  };
  
  // Effect configurations for match effects
  const EFFECT_VISUALS = {
    fire_match: {
      particles: ['🔥', '🔥', '✨', '💥'],
      glowColor: 'rgba(255, 100, 0, 0.6)',
      explosionClass: 'explosion-fire'
    },
    sparkle_trail: {
      particles: ['✨', '⭐', '💫', '✨'],
      glowColor: 'rgba(255, 215, 0, 0.6)',
      explosionClass: 'explosion-sparkle'
    },
    rainbow_burst: {
      particles: ['🌈', '✨', '💖', '⭐'],
      glowColor: 'rgba(255, 0, 128, 0.5)',
      explosionClass: 'explosion-rainbow'
    },
    galaxy_swirl: {
      particles: ['⭐', '🌟', '💫', '✨'],
      glowColor: 'rgba(147, 112, 219, 0.6)',
      explosionClass: 'explosion-cosmic'
    },
    lightning_strike: {
      particles: ['⚡', '💥', '⚡', '✨'],
      glowColor: 'rgba(255, 255, 0, 0.6)',
      explosionClass: 'explosion-lightning'
    }
  };
  
  // Get effect config
  const effectConfig = equippedEffect ? EFFECT_VISUALS[equippedEffect] : null;
  
  // Get applicable skin for this cell
  const getSkinForCell = () => {
    if (!equippedSkin || !cell?.type) return null;
    const skinConfig = SKIN_VISUALS[equippedSkin];
    if (!skinConfig) return null;
    if (skinConfig.appliesTo === 'all' || skinConfig.appliesTo === cell.type) {
      return skinConfig;
    }
    return null;
  };
  
  const activeSkin = getSkinForCell();
  
  // Handle new fruit appearing/falling with bounce
  useEffect(() => {
    if (isNew && !isExploding) {
      setAnimationClass('animate-fruit-bounce-drop');
      const timer = setTimeout(() => setAnimationClass(''), 500);
      return () => clearTimeout(timer);
    }
  }, [isNew, cell?.id, isExploding]);

  // Handle explosion animation with custom effect
  useEffect(() => {
    if (isExploding) {
      setAnimationClass('fruit-explode');
      setShowParticles(true);
      if (effectConfig) {
        setShowEffectParticles(true);
      }
      const timer = setTimeout(() => {
        setShowParticles(false);
        setShowEffectParticles(false);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [isExploding, effectConfig]);

  // Handle gap cells - render as blocked/empty space
  if (cell?.isGap || cell?.special === SPECIAL_TYPES.GAP) {
    return (
      <div className="aspect-square rounded-lg bg-gray-800/50 border-2 border-gray-700/50 relative overflow-hidden">
        {/* Gap pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-2 border-2 border-dashed border-gray-600 rounded" />
        </div>
        {/* X pattern to indicate blocked */}
        <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 100 100">
          <line x1="20" y1="20" x2="80" y2="80" stroke="currentColor" strokeWidth="3" className="text-gray-500" />
          <line x1="80" y1="20" x2="20" y2="80" stroke="currentColor" strokeWidth="3" className="text-gray-500" />
        </svg>
      </div>
    );
  }
  
  // Handle chocolate cells (no fruit, just chocolate blocker)
  if (cell?.isChocolate || cell?.obstacle === OBSTACLE_TYPES.CHOCOLATE) {
    return (
      <div 
        className={cn(
          "aspect-square rounded-lg relative overflow-hidden cursor-pointer transition-transform",
          "bg-gradient-to-br from-amber-800 to-amber-950 border-2 border-amber-700",
          isSelected && "ring-2 ring-yellow-400 scale-105",
          isAdjacent && "ring-1 ring-yellow-300/50"
        )}
        onClick={onClick}
        data-testid="chocolate-cell"
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={cn("select-none", small ? "text-xl" : "text-3xl")}>🍫</span>
        </div>
        {/* Chocolate drip effect */}
        <div className="absolute top-0 left-1/4 w-2 h-3 bg-amber-900 rounded-b-full animate-pulse" />
        <div className="absolute top-0 right-1/3 w-1.5 h-2 bg-amber-900 rounded-b-full animate-pulse delay-75" />
      </div>
    );
  }
  
  // Handle ingredient cells
  if (cell?.isIngredient) {
    return (
      <div 
        className={cn(
          "aspect-square rounded-lg relative overflow-hidden cursor-pointer transition-transform",
          "bg-gradient-to-br from-emerald-100 to-teal-100 border-2 border-emerald-400",
          isSelected && "ring-2 ring-yellow-400 scale-105",
          isAdjacent && "ring-1 ring-yellow-300/50",
          animationClass
        )}
        onClick={onClick}
        data-testid="ingredient-cell"
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={cn("select-none drop-shadow-lg", small ? "text-2xl" : "text-4xl")}>
            {cell.ingredientEmoji}
          </span>
        </div>
        {/* Sparkle effect */}
        <div className="absolute top-1 right-1 w-2 h-2 bg-yellow-300 rounded-full animate-ping" />
      </div>
    );
  }

  if (!cell || !cell.type) {
    return (
      <div className="aspect-square bg-game-cell rounded-lg" />
    );
  }

  const fruitData = FRUITS[cell.type];
  const isSpecial = cell.special !== SPECIAL_TYPES.NORMAL;
  const isStripedH = cell.special === SPECIAL_TYPES.STRIPED_H;
  const isStripedV = cell.special === SPECIAL_TYPES.STRIPED_V;
  const isDisco = cell.special === SPECIAL_TYPES.DISCO;
  const isWrapped = cell.special === SPECIAL_TYPES.WRAPPED;
  
  // Check for obstacles
  const hasIce = cell.obstacle === OBSTACLE_TYPES.ICE;
  const hasChain = cell.obstacle === OBSTACLE_TYPES.CHAIN;
  const hasHoney = cell.obstacle === OBSTACLE_TYPES.HONEY;
  const iceLayers = cell.obstacleData?.layers || 1;
  
  // Dark Forest obstacles
  const hasVine = cell.obstacle === OBSTACLE_TYPES.VINE || cell.isVine;
  const hasTreeSap = cell.obstacle === OBSTACLE_TYPES.TREE_SAP || cell.isTreeSap;
  const isBirdNest = cell.obstacle === OBSTACLE_TYPES.BIRD_NEST || cell.isBirdNest;
  const isHollowKnot = cell.obstacle === OBSTACLE_TYPES.HOLLOW_KNOT || cell.isHollowKnot;
  const nestProgress = cell.obstacleData?.hatchProgress || 0;
  const nestTarget = cell.obstacleData?.hatchTarget || 3;
  
  // New obstacles
  const hasBubble = cell.obstacle === OBSTACLE_TYPES.BUBBLE;
  const hasFrozenFruit = cell.obstacle === OBSTACLE_TYPES.FROZEN_FRUIT;
  const frozenLayers = cell.obstacleData?.frozenLayers || 2;
  const hasCurse = cell.obstacle === OBSTACLE_TYPES.CURSE_TILE;

  // Get particle color based on fruit type
  const getParticleColor = () => {
    const colorMap = {
      banana: '#fbbf24',
      apple: '#ef4444',
      pear: '#84cc16',
      pineapple: '#f97316',
      orange: '#fb923c',
      peach: '#a855f7',
      strawberry: '#f43f5e',
      lemon: '#facc15',
      cherry: '#be185d',
      watermelon: '#22c55e',
      mango: '#fb923c',
      kiwi: '#4ade80',
      blueberry: '#6366f1',
    };
    return colorMap[fruitData?.color] || '#8b5cf6';
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || isExploding}
      className={cn(
        "aspect-square rounded-lg sm:rounded-xl flex items-center justify-center relative overflow-hidden",
        "game-cell-interactive no-select",
        "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        "disabled:cursor-not-allowed",
        "transition-all duration-150",
        
        // 3D Effects
        "fruit-cell-3d fruit-3d-shadow fruit-3d-bevel fruit-3d-hover",
        
        // Fruit-specific 3D backgrounds (when not exploding and no skin)
        !isExploding && !activeSkin && cell.type === 'banana' && "fruit-3d-banana",
        !isExploding && !activeSkin && cell.type === 'apple' && "fruit-3d-apple",
        !isExploding && !activeSkin && cell.type === 'pear' && "fruit-3d-pear",
        !isExploding && !activeSkin && cell.type === 'orange' && "fruit-3d-orange",
        !isExploding && !activeSkin && cell.type === 'pineapple' && "fruit-3d-pineapple",
        !isExploding && !activeSkin && cell.type === 'peach' && "fruit-3d-peach",
        !isExploding && !activeSkin && cell.type === 'strawberry' && "fruit-3d-strawberry",
        !isExploding && !activeSkin && cell.type === 'lemon' && "fruit-3d-lemon",
        !isExploding && !activeSkin && cell.type === 'cherry' && "fruit-3d-cherry",
        !isExploding && !activeSkin && cell.type === 'watermelon' && "fruit-3d-watermelon",
        !isExploding && !activeSkin && cell.type === 'mango' && "fruit-3d-mango",
        !isExploding && !activeSkin && cell.type === 'kiwi' && "fruit-3d-kiwi",
        !isExploding && !activeSkin && cell.type === 'blueberry' && "fruit-3d-blueberry",
        
        // Base state fallback
        !isExploding && activeSkin && "bg-game-cell hover:bg-game-cell-hover",
        
        // Exploding state - now with pop animation
        isExploding && "bg-warning/30 scale-110 animate-cell-pop",
        
        // Special fruit backgrounds
        !isExploding && isStripedH && "striped-h-bg",
        !isExploding && isStripedV && "striped-v-bg",
        !isExploding && isDisco && "disco-bg",
        !isExploding && isWrapped && "wrapped-bg",
        
        // Skin effects - apply special background when skin is active
        !isExploding && activeSkin?.bgClass && activeSkin.bgClass,
        !isExploding && activeSkin?.borderClass && activeSkin.borderClass,
        !isExploding && activeSkin?.glowClass && activeSkin.glowClass,
        !isExploding && activeSkin?.neonPulse && "animate-neon-pulse",
        
        // Selected state - now with wobble animation
        isSelected && !isExploding && [
          "bg-game-selected",
          "ring-2 ring-accent ring-offset-1",
          "scale-110 z-10",
          "selected-glow",
          "animate-selection-wobble",
          "fruit-3d-pressed",
        ],
        
        // Adjacent hint
        isAdjacent && !isExploding && [
          "bg-game-cell-hover",
          "ring-1 ring-accent/40",
        ],
        
        // Fruit-specific glow when selected
        isSelected && !isExploding && cell.type === 'banana' && "fruit-glow-banana",
        isSelected && !isExploding && cell.type === 'apple' && "fruit-glow-apple",
        isSelected && !isExploding && cell.type === 'pear' && "fruit-glow-pear",
        isSelected && !isExploding && cell.type === 'pineapple' && "fruit-glow-pineapple",
        isSelected && !isExploding && cell.type === 'orange' && "fruit-glow-orange",
      )}
      aria-label={`${fruitData?.name || 'Fruit'} ${isSpecial ? `(${cell.special})` : ''} at position`}
      data-testid={`fruit-cell-${cell.type}${activeSkin ? `-${equippedSkin}` : ''}`}
    >
      {/* Striped overlay for horizontal - ON TOP of fruit with transparency */}
      {isStripedH && !isExploding && (
        <div className="absolute inset-0 pointer-events-none z-20 rounded-lg overflow-hidden">
          {/* Semi-transparent stripes over the fruit */}
          <div className="absolute inset-0 flex flex-col justify-evenly">
            <div className="h-1.5 bg-yellow-400/70 border-y border-yellow-600/80" />
            <div className="h-1.5 bg-yellow-400/70 border-y border-yellow-600/80" />
            <div className="h-1.5 bg-yellow-400/70 border-y border-yellow-600/80" />
          </div>
          {/* Small direction arrows */}
          <div className="absolute left-0.5 top-1/2 -translate-y-1/2 text-yellow-600 text-[8px] font-black drop-shadow-md">◀</div>
          <div className="absolute right-0.5 top-1/2 -translate-y-1/2 text-yellow-600 text-[8px] font-black drop-shadow-md">▶</div>
        </div>
      )}
      
      {/* Striped overlay for vertical - ON TOP of fruit with transparency */}
      {isStripedV && !isExploding && (
        <div className="absolute inset-0 pointer-events-none z-20 rounded-lg overflow-hidden">
          {/* Semi-transparent stripes over the fruit */}
          <div className="absolute inset-0 flex flex-row justify-evenly">
            <div className="w-1.5 bg-yellow-400/70 border-x border-yellow-600/80" />
            <div className="w-1.5 bg-yellow-400/70 border-x border-yellow-600/80" />
            <div className="w-1.5 bg-yellow-400/70 border-x border-yellow-600/80" />
          </div>
          {/* Small direction arrows */}
          <div className="absolute top-0.5 left-1/2 -translate-x-1/2 text-yellow-600 text-[8px] font-black drop-shadow-md">▲</div>
          <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 text-yellow-600 text-[8px] font-black drop-shadow-md">▼</div>
        </div>
      )}
      
      {/* Disco rainbow overlay */}
      {isDisco && !isExploding && (
        <div className="absolute inset-0 pointer-events-none disco-rainbow-bg animate-spin-slow" />
      )}
      
      {/* Wrapped/Packed fruit overlay - Thin ribbons so fruit is visible */}
      {isWrapped && !isExploding && (
        <div className="absolute inset-0 pointer-events-none z-20 rounded-lg overflow-hidden">
          {/* Thin horizontal ribbon */}
          <div className="absolute top-1/2 left-0 right-0 h-1.5 bg-fuchsia-500/90 -translate-y-1/2 border-y border-fuchsia-700" />
          {/* Thin vertical ribbon */}
          <div className="absolute left-1/2 top-0 bottom-0 w-1.5 bg-fuchsia-500/90 -translate-x-1/2 border-x border-fuchsia-700" />
          {/* Small center bow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[8px]">🎀</div>
        </div>
      )}
      
      {/* Skin shimmer overlay */}
      {activeSkin?.shimmer && !isExploding && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-lg">
          <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/40 to-transparent" 
               style={{ backgroundSize: '200% 100%' }} />
        </div>
      )}
      
      {/* 3D Glossy reflection overlay */}
      {!activeSkin && !isExploding && (
        <div className="fruit-3d-gloss absolute inset-0 pointer-events-none rounded-lg" />
      )}
      
      {/* Skin sparkle indicator */}
      {activeSkin?.overlayEmoji && !isExploding && (
        <div className={cn(
          "absolute -top-1 -right-1 animate-pulse z-20 drop-shadow-lg",
          extraSmall ? "text-[8px]" : "text-sm"
        )}>
          {activeSkin.overlayEmoji}
        </div>
      )}
      
      {/* Fruit emoji with animations */}
      <span 
        className={cn(
          "select-none relative z-10",
          "transition-transform duration-150",
          isSelected && !isExploding && "animate-wiggle",
          !disabled && !isExploding && "hover:scale-110",
          isDisco && !isExploding && "animate-pulse",
          isWrapped && !isExploding && "animate-bounce",
          animationClass,
          // Size based on grid (only for emoji)
          !USE_3D_FRUITS && (extraSmall ? "text-lg" : small ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl lg:text-4xl"),
        )}
        role="img"
        aria-label={fruitData?.name || 'Fruit'}
      >
        {isDisco ? (
          USE_3D_FRUITS ? (
            <img 
              src={FRUIT_3D_IMAGES.watermelon} 
              alt="Disco" 
              className={cn(
                "w-full h-full object-contain drop-shadow-lg",
                "filter hue-rotate-180 saturate-150"
              )}
            />
          ) : "🍈"
        ) : USE_3D_FRUITS && FRUIT_3D_IMAGES[cell.type] ? (
          <img 
            src={FRUIT_3D_IMAGES[cell.type]} 
            alt={fruitData?.name || cell.type}
            className={cn(
              "w-full h-full object-contain drop-shadow-md",
              small ? "p-0" : "p-0.5"
            )}
          />
        ) : fruitData?.emoji}
      </span>
      
      {/* Special indicator badge */}
      {isSpecial && !isDisco && !isWrapped && !isExploding && (
        <div className={cn(
          "absolute rounded-full bg-accent flex items-center justify-center",
          small ? "bottom-0 right-0 w-2.5 h-2.5" : "bottom-0.5 right-0.5 w-3 h-3"
        )}>
          <span className={small ? "text-[6px]" : "text-[8px]"}>{isStripedH ? '↔' : '↕'}</span>
        </div>
      )}
      
      {/* Wrapped indicator badge (bag icon) */}
      {isWrapped && !isExploding && (
        <div className={cn(
          "absolute rounded-full bg-purple-500 flex items-center justify-center shadow-lg",
          small ? "bottom-0 right-0 w-3 h-3" : "bottom-0.5 right-0.5 w-4 h-4"
        )}>
          <span className={small ? "text-[7px]" : "text-[9px]"}>💥</span>
        </div>
      )}

      {/* Explosion particles */}
      {showParticles && (
        <div className="absolute inset-0 pointer-events-none z-20">
          {[...Array(8)].map((_, i) => {
            const angle = (i / 8) * 360;
            const distance = small ? 20 + Math.random() * 15 : 30 + Math.random() * 20;
            const tx = Math.cos(angle * Math.PI / 180) * distance;
            const ty = Math.sin(angle * Math.PI / 180) * distance;
            return (
              <div 
                key={i}
                className={cn(
                  "absolute left-1/2 top-1/2 rounded-full particle-explode",
                  small ? "w-1.5 h-1.5" : "w-2 h-2",
                  effectConfig?.explosionClass
                )}
                style={{
                  '--tx': `${tx}px`,
                  '--ty': `${ty}px`,
                  backgroundColor: effectConfig?.glowColor || getParticleColor(),
                  animationDelay: `${i * 30}ms`,
                }}
              />
            );
          })}
          {/* Center flash */}
          <div className={cn(
            "absolute inset-0 rounded-lg animate-flash",
            effectConfig ? "bg-gradient-radial from-white/80 to-transparent" : "bg-white/60"
          )} 
          style={effectConfig ? { boxShadow: `0 0 20px ${effectConfig.glowColor}` } : {}}
          />
        </div>
      )}
      
      {/* Custom effect emoji particles */}
      {showEffectParticles && effectConfig && (
        <div className="absolute inset-0 pointer-events-none z-30">
          {effectConfig.particles.map((particle, i) => {
            const angle = (i / effectConfig.particles.length) * 360;
            const distance = small ? 25 : 40;
            const tx = Math.cos(angle * Math.PI / 180) * distance;
            const ty = Math.sin(angle * Math.PI / 180) * distance;
            return (
              <div 
                key={i}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 particle-explode"
                style={{
                  '--tx': `${tx}px`,
                  '--ty': `${ty}px`,
                  animationDelay: `${i * 50}ms`,
                  fontSize: small ? '12px' : '16px',
                }}
              >
                {particle}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Ice overlay */}
      {hasIce && !isExploding && (
        <div className="absolute inset-0 pointer-events-none z-30" data-testid="ice-overlay">
          {/* Ice layer visual based on number of layers - made more visible */}
          <div 
            className={cn(
              "absolute inset-0 rounded-lg border-4",
              iceLayers >= 3 && "bg-cyan-300/80 border-cyan-600",
              iceLayers === 2 && "bg-cyan-200/70 border-cyan-500",
              iceLayers === 1 && "bg-cyan-200/60 border-cyan-400"
            )}
          >
            {/* Ice crack pattern - made more visible */}
            <svg className="absolute inset-0 w-full h-full opacity-70" viewBox="0 0 100 100">
              <path d="M50 10 L45 30 L55 35 L50 50 L60 55 L50 70 L45 90" 
                stroke="#0891b2" strokeWidth="2.5" fill="none" />
              <path d="M30 25 L50 35 L70 25" 
                stroke="#0891b2" strokeWidth="2" fill="none" />
              {iceLayers >= 2 && (
                <path d="M25 50 L50 55 L75 50" 
                  stroke="#0891b2" strokeWidth="2" fill="none" />
              )}
            </svg>
            {/* Layer indicator */}
            <div className={cn(
              "absolute top-0 right-0 bg-cyan-600 text-white rounded-bl-lg flex items-center justify-center font-bold shadow-md",
              small ? "w-3 h-3 text-[8px]" : "w-4 h-4 text-[10px]"
            )}>
              {iceLayers}
            </div>
          </div>
          {/* Ice emoji indicator */}
          <div className="absolute -top-1 -left-1 text-sm drop-shadow-md">🧊</div>
          {/* Frost sparkle */}
          <div className="absolute top-1 left-1 w-2 h-2 bg-white rounded-full animate-pulse" />
          <div className="absolute bottom-2 right-2 w-1.5 h-1.5 bg-cyan-200 rounded-full animate-ping" />
        </div>
      )}
      
      {/* Chain overlay */}
      {hasChain && !isExploding && (
        <div className="absolute inset-0 pointer-events-none z-30" data-testid="chain-overlay">
          {/* Darker background with visible border */}
          <div className="absolute inset-0 bg-gray-600/50 rounded-lg border-4 border-gray-700">
            {/* Chain pattern - bold and visible */}
            <svg className="absolute w-full h-full" viewBox="0 0 100 100">
              {/* Horizontal chains for better visibility */}
              <ellipse cx="30" cy="35" rx="18" ry="10" fill="none" stroke="#333" strokeWidth="8" />
              <ellipse cx="70" cy="35" rx="18" ry="10" fill="none" stroke="#333" strokeWidth="8" />
              <ellipse cx="30" cy="65" rx="18" ry="10" fill="none" stroke="#333" strokeWidth="8" />
              <ellipse cx="70" cy="65" rx="18" ry="10" fill="none" stroke="#333" strokeWidth="8" />
              {/* Connecting lines */}
              <line x1="48" y1="35" x2="52" y2="35" stroke="#333" strokeWidth="6" />
              <line x1="48" y1="65" x2="52" y2="65" stroke="#333" strokeWidth="6" />
              <line x1="30" y1="45" x2="30" y2="55" stroke="#333" strokeWidth="6" />
              <line x1="70" y1="45" x2="70" y2="55" stroke="#333" strokeWidth="6" />
            </svg>
          </div>
          {/* Chain emoji indicator - larger and more visible */}
          <div className="absolute -top-2 -left-2 text-lg drop-shadow-lg bg-gray-800 rounded-full p-0.5">⛓️</div>
          {/* Lock icon */}
          <div className={cn(
            "absolute bottom-0 right-0 bg-gray-800 text-white rounded-tl-lg flex items-center justify-center shadow-lg",
            small ? "w-4 h-4" : "w-5 h-5"
          )}>
            <span className={small ? "text-[10px]" : "text-xs"}>🔒</span>
          </div>
        </div>
      )}
      
      {/* Honey overlay */}
      {hasHoney && !isExploding && (
        <div className="absolute inset-0 pointer-events-none z-30" data-testid="honey-overlay">
          {/* Strong amber background with border */}
          <div className="absolute inset-0 bg-amber-400/60 rounded-lg border-4 border-amber-600" />
          {/* Honey drips - more visible */}
          <div className="absolute top-0 left-1/4 w-4 h-6 bg-amber-600/90 rounded-b-full" />
          <div className="absolute top-0 right-1/4 w-3 h-5 bg-amber-600/90 rounded-b-full" />
          <div className="absolute bottom-0 left-1/3 w-4 h-4 bg-amber-600/90 rounded-t-full" />
          <div className="absolute bottom-0 right-1/4 w-3 h-3 bg-amber-600/90 rounded-t-full" />
          {/* Honey emoji indicator - larger */}
          <div className="absolute -top-2 -left-2 text-lg drop-shadow-lg bg-amber-500 rounded-full p-0.5">🍯</div>
          {/* Sticky indicator */}
          <div className={cn(
            "absolute bottom-0 right-0 bg-amber-700 text-white rounded-tl-lg flex items-center justify-center shadow-lg",
            small ? "w-4 h-4" : "w-5 h-5"
          )}>
            <span className={small ? "text-[8px]" : "text-[10px]"}>⚠️</span>
          </div>
        </div>
      )}
      
      {/* === DARK FOREST OBSTACLES === */}
      
      {/* Vine overlay - connected cells with green vines */}
      {hasVine && !isExploding && (
        <div className="absolute inset-0 pointer-events-none z-30" data-testid="vine-overlay">
          {/* Dark green vine background */}
          <div className="absolute inset-0 bg-emerald-800/40 rounded-lg border-2 border-emerald-600 animate-vine-sway" />
          {/* Vine tendrils */}
          <svg className="absolute w-full h-full" viewBox="0 0 100 100">
            <path d="M10 50 Q25 30 50 50 Q75 70 90 50" 
              fill="none" stroke="#166534" strokeWidth="4" opacity="0.8" />
            <path d="M50 10 Q30 35 50 50 Q70 65 50 90" 
              fill="none" stroke="#166534" strokeWidth="3" opacity="0.7" />
            {/* Leaves */}
            <ellipse cx="25" cy="35" rx="8" ry="5" fill="#22c55e" opacity="0.8" transform="rotate(-30 25 35)" />
            <ellipse cx="75" cy="65" rx="8" ry="5" fill="#22c55e" opacity="0.8" transform="rotate(30 75 65)" />
          </svg>
          {/* Vine emoji */}
          <div className="absolute -top-2 -left-2 text-lg drop-shadow-lg">🌿</div>
          {/* Connected indicator */}
          <div className={cn(
            "absolute bottom-0 right-0 bg-emerald-700 text-white rounded-tl-lg flex items-center justify-center shadow-lg",
            small ? "w-4 h-4" : "w-5 h-5"
          )}>
            <span className={small ? "text-[8px]" : "text-[10px]"}>🔗</span>
          </div>
        </div>
      )}
      
      {/* Tree Sap overlay - amber sticky slow effect */}
      {hasTreeSap && !isExploding && (
        <div className="absolute inset-0 pointer-events-none z-30" data-testid="tree-sap-overlay">
          {/* Amber sap background with gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-yellow-700/50 to-amber-900/60 rounded-lg border-2 border-amber-800" />
          {/* Sap drip animation */}
          <div className="absolute top-0 left-1/3 w-3 h-8 bg-amber-700/80 rounded-b-full animate-sap-drip" />
          <div className="absolute top-0 right-1/4 w-2 h-6 bg-amber-800/70 rounded-b-full animate-sap-drip" style={{ animationDelay: '0.5s' }} />
          {/* Fossilized look */}
          <div className="absolute inset-2 border border-amber-600/30 rounded" />
          {/* Sap emoji */}
          <div className="absolute -top-2 -left-2 text-lg drop-shadow-lg">🍯</div>
          {/* Slow indicator */}
          <div className={cn(
            "absolute bottom-0 right-0 bg-amber-900 text-white rounded-tl-lg flex items-center justify-center shadow-lg",
            small ? "w-4 h-4" : "w-5 h-5"
          )}>
            <span className={small ? "text-[8px]" : "text-[10px]"}>🐢</span>
          </div>
        </div>
      )}
      
      {/* Bird Nest - special blocker that hatches */}
      {isBirdNest && !isExploding && (
        <div className="absolute inset-0 pointer-events-none z-20" data-testid="bird-nest-overlay">
          {/* Nest background */}
          <div className="absolute inset-0 bg-gradient-to-b from-amber-800/80 to-amber-950/90 rounded-lg border-2 border-amber-700">
            {/* Nest texture */}
            <svg className="absolute w-full h-full" viewBox="0 0 100 100">
              <path d="M20 60 Q50 80 80 60" fill="none" stroke="#92400e" strokeWidth="3" />
              <path d="M25 55 Q50 70 75 55" fill="none" stroke="#78350f" strokeWidth="2" />
              <path d="M30 50 Q50 60 70 50" fill="none" stroke="#92400e" strokeWidth="2" />
            </svg>
          </div>
          {/* Eggs based on progress */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex gap-1">
              {[...Array(nestTarget)].map((_, i) => (
                <span 
                  key={i} 
                  className={cn(
                    "text-lg transition-all",
                    i < nestProgress ? "scale-110 animate-pulse" : "opacity-50 grayscale"
                  )}
                >
                  🥚
                </span>
              ))}
            </div>
          </div>
          {/* Nest emoji */}
          <div className="absolute -top-2 -left-2 text-lg drop-shadow-lg">🪺</div>
          {/* Progress indicator */}
          <div className={cn(
            "absolute bottom-0 right-0 bg-amber-800 text-white rounded-tl-lg flex items-center justify-center shadow-lg px-1",
            small ? "h-4 text-[8px]" : "h-5 text-[10px]"
          )}>
            {nestProgress}/{nestTarget}
          </div>
        </div>
      )}
      
      {/* Hollow Knot - teleport portal */}
      {isHollowKnot && !isExploding && (
        <div className="absolute inset-0 pointer-events-none z-20" data-testid="hollow-knot-overlay">
          {/* Dark portal background */}
          <div className="absolute inset-0 bg-gradient-radial from-purple-900/90 via-slate-900/95 to-black rounded-full border-2 border-purple-700">
            {/* Swirl effect */}
            <div className="absolute inset-2 rounded-full border border-purple-500/30 animate-spin" style={{ animationDuration: '8s' }} />
            <div className="absolute inset-3 rounded-full border border-purple-400/20 animate-spin" style={{ animationDuration: '6s', animationDirection: 'reverse' }} />
            <div className="absolute inset-4 rounded-full border border-purple-300/10 animate-spin" style={{ animationDuration: '4s' }} />
          </div>
          {/* Center glow */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-4 h-4 bg-purple-400 rounded-full animate-pulse blur-sm" />
          </div>
          {/* Portal emoji */}
          <div className="absolute -top-2 -left-2 text-lg drop-shadow-lg">🕳️</div>
        </div>
      )}
      
      {/* BUBBLE overlay - fruit floats up */}
      {hasBubble && !isExploding && (
        <div className="absolute inset-0 pointer-events-none z-20" data-testid="bubble-overlay">
          {/* Bubble sphere */}
          <div className="absolute inset-0 rounded-full border-2 border-cyan-300/60 bg-cyan-100/20">
            {/* Shine effect */}
            <div className="absolute top-1 left-1 w-2 h-2 bg-white/60 rounded-full" />
            <div className="absolute top-2 left-3 w-1 h-1 bg-white/40 rounded-full" />
          </div>
          {/* Floating animation indicator */}
          <div className="absolute -top-1 left-1/2 -translate-x-1/2 text-xs animate-bounce">⬆️</div>
          {/* Bubble emoji */}
          <div className="absolute -bottom-1 -right-1 text-sm drop-shadow-lg">🫧</div>
        </div>
      )}
      
      {/* FROZEN_FRUIT overlay - needs multiple matches to thaw */}
      {hasFrozenFruit && !isExploding && (
        <div className="absolute inset-0 pointer-events-none z-20" data-testid="frozen-fruit-overlay">
          {/* Ice crystal effect */}
          <div className={cn(
            "absolute inset-0 rounded-lg border-2",
            frozenLayers >= 2 
              ? "bg-cyan-200/60 border-cyan-400" 
              : "bg-cyan-100/40 border-cyan-300"
          )}>
            {/* Frost cracks */}
            <svg className="absolute inset-0 w-full h-full opacity-30" viewBox="0 0 100 100">
              <line x1="10" y1="50" x2="90" y2="50" stroke="#06b6d4" strokeWidth="1" />
              <line x1="50" y1="10" x2="50" y2="90" stroke="#06b6d4" strokeWidth="1" />
              <line x1="20" y1="20" x2="80" y2="80" stroke="#06b6d4" strokeWidth="0.5" />
              <line x1="80" y1="20" x2="20" y2="80" stroke="#06b6d4" strokeWidth="0.5" />
            </svg>
          </div>
          {/* Frost indicator */}
          <div className="absolute -top-1 -right-1 text-sm drop-shadow-lg">❄️</div>
          {/* Layer counter */}
          <div className={cn(
            "absolute bottom-0 right-0 bg-cyan-600 text-white rounded-tl-lg flex items-center justify-center shadow-lg px-1",
            small ? "h-4 text-[8px]" : "h-5 text-[10px]"
          )}>
            {frozenLayers}x
          </div>
        </div>
      )}
      
      {/* CURSE_TILE overlay - spreads each turn */}
      {hasCurse && !isExploding && (
        <div className="absolute inset-0 pointer-events-none z-20" data-testid="curse-overlay">
          {/* Dark curse effect */}
          <div className="absolute inset-0 rounded-lg bg-purple-900/50 border-2 border-purple-600 animate-pulse">
            {/* Swirling dark energy */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full h-full rounded-lg bg-gradient-radial from-purple-500/30 via-transparent to-transparent animate-spin" style={{ animationDuration: '3s' }} />
            </div>
          </div>
          {/* Curse particles */}
          <div className="absolute top-0 left-1/4 w-1 h-1 bg-purple-400 rounded-full animate-ping" />
          <div className="absolute bottom-1 right-1/3 w-1 h-1 bg-purple-500 rounded-full animate-ping" style={{ animationDelay: '0.5s' }} />
          {/* Curse emoji */}
          <div className="absolute -top-1 -left-1 text-sm drop-shadow-lg animate-pulse">💀</div>
          {/* Warning */}
          <div className="absolute -bottom-1 -right-1 text-xs">☠️</div>
        </div>
      )}
    </button>
  );
}

// Memoization comparison function to prevent unnecessary re-renders
const areCellPropsEqual = (prevProps, nextProps) => {
  // Check primitive props first (fast comparisons)
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  if (prevProps.isAdjacent !== nextProps.isAdjacent) return false;
  if (prevProps.disabled !== nextProps.disabled) return false;
  if (prevProps.isNew !== nextProps.isNew) return false;
  if (prevProps.isExploding !== nextProps.isExploding) return false;
  if (prevProps.small !== nextProps.small) return false;
  if (prevProps.equippedSkin !== nextProps.equippedSkin) return false;
  if (prevProps.equippedEffect !== nextProps.equippedEffect) return false;
  
  // Check cell properties (most important for re-render decision)
  const prevCell = prevProps.cell;
  const nextCell = nextProps.cell;
  
  // Handle null/undefined cells
  if (!prevCell && !nextCell) return true;
  if (!prevCell || !nextCell) return false;
  
  // Compare cell ID first (fastest unique check)
  if (prevCell.id !== nextCell.id) return false;
  
  // Compare critical cell properties
  if (prevCell.type !== nextCell.type) return false;
  if (prevCell.special !== nextCell.special) return false;
  if (prevCell.obstacle !== nextCell.obstacle) return false;
  if (prevCell.isGap !== nextCell.isGap) return false;
  
  // Check obstacle data if present
  if (prevCell.obstacleData?.iceLayers !== nextCell.obstacleData?.iceLayers) return false;
  if (prevCell.obstacleData?.hatchProgress !== nextCell.obstacleData?.hatchProgress) return false;
  
  return true;
};

// Memoized FruitCell component - prevents unnecessary re-renders
const MemoizedFruitCell = memo(FruitCellComponent, areCellPropsEqual);

// Export both the memoized version (default) and raw component
export { MemoizedFruitCell as FruitCell, FruitCellComponent };
